import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { marcarElegido } from '@/lib/idiomaSesion';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

/** La misma clave que lee `lookupLocalStorage` en `i18n.js` y el loader de
 *  CookieYes en `index.html`. */
const CLAVE_IDIOMA = 'i18nextLng';

export const LanguageProvider = ({ children }) => {
  const { t, i18n } = useTranslation();

  /**
   * Cambia el idioma **y lo guarda**, que es la única vez que se guarda.
   *
   * El detector de i18next ya no cachea (`caches: []` en `i18n.js`, con el
   * porqué escrito allí): con la URL decidiendo el idioma en las páginas
   * públicas, cachear la detección le sobrescribiría la preferencia a quien
   * abriera un enlace en otro idioma. Aquí, en cambio, el idioma lo ha elegido
   * una persona pulsando, así que sí es una preferencia y sí se recuerda.
   *
   * El `try/catch` no es ceremonia: en modo incógnito o con las cookies
   * bloqueadas, `localStorage` lanza. Perder la preferencia es aceptable;
   * romper el cambio de idioma, no.
   */
  const setLanguage = (lang) => {
    // Primero la sesión: es lo que respeta el forzado por IP de Portugal el
    // resto de esta sesión y lo que impide que la home vuelva a mandarle a
    // /pt/ (ver `lib/idiomaSesion.js`). No lanza aunque no haya almacenamiento.
    marcarElegido(lang);
    i18n.changeLanguage(lang);
    try {
      window.localStorage.setItem(CLAVE_IDIOMA, lang);
    } catch {
      /* Sin almacenamiento: el idioma vale para esta sesión y ya. */
    }
  };

  const language = (i18n.language || 'es').split('-')[0];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};